import { Controller } from "@hotwired/stimulus"
import { dispatchElement, upflow } from "../shared/domHelper"
import { isPlainObject, toCamelCase } from "../shared/functions"

export default class DispatchController extends Controller {
  // { dispatchEventName: { flowSelector:String|()=>false|unknown, flowProc:({ flow, event })=>{} } }
  delegateUpflowConfig = {}
  dispatchHooks = {}

  static values = {
    delegateUpflow: String
  }

  connect() {
    // recall event
    this.element.addEventListener("dispatch:recall", (event) => {
      let targetMatch = false
      if (typeof event.detail.target === "function") {
        targetMatch = event.detail.target(this.element)
      } else {
        targetMatch = (event.detail.target === this.element) || (this.element.id === event.detail?.id)
      }
      if (targetMatch) {
        event.stopPropagation()
        if (typeof event.detail.on === "function") return event.detail.on(this)
        console.warn("dispatch:recall event requires a controller or turboFrame function in its detail.")
      }
    })

    // delegate upflow event
    if (this.hasDelegateUpflowValue && this.delegateUpflowValue.length) {
      const parsedData = new Function(`return (${this.delegateUpflowValue})`).call({ element: this.element })
      if (isPlainObject(parsedData)) {
        Object.entries(parsedData).forEach(([eventName, entryOrObject]) => {
          if (eventName.indexOf("dispatch:delegate:") !== 0) {
            throw new Error(`${eventName} keys must start with 'dispatch:delegate:'. E.g. { \"dispatch:delegate:<name>\": {selector: '<flowSelector>', on:({ flow, event })=>{ ... }} }`)
          }
          if (!Array.isArray(entryOrObject) && !isPlainObject(entryOrObject)) {
            throw new Error(`${entryOrObject} entry must be an array or object. E.g. 
              { \"dispatch:delegate:<name>\": {selector: '<flowSelector>', on:({ flow, event })=>{ ... }} } 
              { \"dispatch:delegate:<name>\": ['<flowSelector>', ({ flow, event })=>{ ... }] } 
            `.trim())
          }
          // 
          let flowSelector
          let flowProc
          if (isPlainObject(entryOrObject)) {
            flowSelector = entryOrObject["selector"]
            flowProc = entryOrObject["on"]
          } else {
            flowSelector = entryOrObject[0]
            flowProc = entryOrObject[1]
          }

          //
          if (typeof flowSelector !== "function" && (typeof flowSelector !== "string" || !flowSelector.length)) {
            throw new Error(`${flowSelector} flowSelector must be a non-empty string or a function.`)
          }
          if (typeof flowProc !== "function") {
            throw new Error(`${flowProc} flowProc must be a function.`)
          }
          this.delegateUpflowConfig[eventName] = { flowSelector, flowProc }
          this.element.addEventListener(eventName, (event) => { this.upflow(event) })
        })
      } else {
        throw new Error("dispatch#delegateUpflowValue must be a valid object. E.g. { \"dispatch:delegate:<name>\": {selector: '<flowSelector>', on:({ flow, event })=>{ ... }} } ")
      }
    }
  }

  addDispatchHook(name, func) {
    if (typeof func !== "function") throw new Error("addDispatchHook requires a function as the second argument.")
    if (!this.dispatchHooks[name]) this.dispatchHooks[name] = []
    this.dispatchHooks[name].push(func)
    return this
  }
  /*
    usage

    1. direct
    data-action="click->dispatch#upflow"
    data-dispatch-upflow-name-param="modal_staff_payroll_flow"
    data-dispatch-upflow-eval-param="flow.onReject(event.detail)"

    2. delegate
    data-action="dispatch:delegate:close->dispatch#upflow"
    data-dispatch-delegate-close-upflow-name-param="modal_staff_payroll_flow"
    data-dispatch-delegate-close-upflow-eval-param="flow.onReject(event.detail)"

    3. delegate with before hook
    this.$emit("dispatch:recall", { 
      target: (element)=>true,
      on:(controller)=>{
        controller.addDispatchHook("dispatch:delegate:close:before", ()=>{
          return false // prevent close upflow
        })
      }
    })
*/
  upflow(event) {
    event.stopPropagation();

    return (async () => {
      const {
        upflowName,
        upflowEval,
      } = await (async () => {
        if (event.type.startsWith("dispatch:delegate:")) {
          let hasPrevented = false
          if (event.type + ":before") {
            const hooks = this.dispatchHooks[event.type + ":before"] || []
            for (const hook of hooks) {
              if (await hook() === false) {
                hasPrevented = true
                break
              }
            }
          }

          // before hook에 의한 prevent 
          if (hasPrevented) {
            return {
              upflowName: "",
              upflowEval: "",
            }
          }

          const valueConfig = this.delegateUpflowConfig[event.type]
          if (valueConfig) {

            return {
              upflowName: valueConfig["flowSelector"],
              upflowEval: valueConfig["flowProc"],
            }
          } else {
            const grapDelegateName = event.type.replace("dispatch:", "").replace(/:/g, "-")
            const datasetPrefix = toCamelCase(grapDelegateName)
            return {
              upflowName: event.params[datasetPrefix + "UpflowName"] || "",
              upflowEval: event.params[datasetPrefix + "UpflowEval"],
            }
          }
        } else {
          return {
            upflowName: event.params.upflowName || "",
            upflowEval: event.params.upflowEval,
          }
        }
      })()

      if (upflowName) {
        upflow(event.currentTarget || event.target, (flow) => {
          if (typeof upflowEval == "function") {
            upflowEval({ flow, event })
          } else if (typeof upflowEval == "string" && upflowEval.length > 0) {
            try {
              // eslint-disable-next-line no-new-func
              const func = new Function("flow", "event", upflowEval)
              return func(flow, event)
            } catch (error) {
              console.error("[dispatch] upflow eval error", { error, event })
            }
          } else {
            console.log("[dispatch] upflow no eval", { flow, event })
          }
        }, upflowName)
      }
    })()
  }

  emit(event) {
    const rootElement = this.element
    const emitName = event.params.emitName
    const emitDetail = event.detail || {}
    dispatchElement(rootElement, emitName, isPlainObject(emitDetail) ? emitDetail : {})
  }
}
